const normalize = (locale: string) => locale.replaceAll('_', '-').toLowerCase();

/** Collapse explicitly equivalent picker choices without changing the active locale. */
export function localePickerOptions(
  locale: string,
  available: readonly string[],
  aliases: Readonly<Record<string, string>> = {}
) {
  // A target must itself remain visible; ignore missing targets and alias chains.
  const equivalents = Object.fromEntries(
    Object.entries(aliases).filter(
      ([source, target]) =>
        available.includes(source) &&
        available.includes(target) &&
        !aliases[target]
    )
  );
  return {
    pickerLocale: equivalents[locale] ?? locale,
    pickerLocales: available.filter((code) => !equivalents[code])
  };
}

/** Match ordered language preferences before using any regional hint. */
export function matchLocale(
  preferred: readonly string[],
  available: readonly string[],
  fallback: string
) {
  for (const preference of preferred) {
    let tag = normalize(preference);
    if (!/^[a-z]{2,8}(?:-[a-z0-9]{1,8})*$/.test(tag)) continue;
    while (tag) {
      const exact = available.find((locale) => normalize(locale) === tag);
      if (exact) return exact;
      const separator = tag.lastIndexOf('-');
      if (separator < 0) break;
      tag = tag.slice(0, separator);
    }
    const variant = available.find((locale) =>
      normalize(locale).startsWith(`${tag}-`)
    );
    if (variant) return variant;
  }
  return fallback;
}

/** Refine an automatic choice's region without changing its language or script. */
export function localeWithTimeZone(
  locale: string,
  available: readonly string[],
  regions: Readonly<Record<string, string>> = {},
  timeZone?: string | null
) {
  if (!timeZone) return locale;
  let region = regions[timeZone];
  if (!region) {
    try {
      // Resolve aliases when the runtime knows them. Never infer from UTC offsets:
      // multiple countries share offsets, and some zone aliases cross borders.
      const canonical = new Intl.DateTimeFormat('en', {
        timeZone
      }).resolvedOptions().timeZone;
      region = regions[canonical];
    } catch {
      return locale;
    }
  }
  if (!region) return locale;
  const parts = normalize(locale).split('-');
  const language = parts[0];
  const script = parts[1]?.length === 4 ? parts[1] : undefined;
  const regional = [language, script, region.toLowerCase()]
    .filter(Boolean)
    .join('-');
  return (
    available.find((candidate) => normalize(candidate) === regional) ?? locale
  );
}
